<template>
  <div class="w-full">
    <Head title="Posty karuzeli" />
    <h1 class="mb-8 text-3xl font-bold">
      Posty karuzeli
    </h1>

    <div class="mb-6 flex flex-wrap items-end gap-x-4 gap-y-2">
      <div>
        <label
          for="carousel-post-filter-title"
          class="mb-1 block text-xs font-medium text-gray-600"
        >
          Tytuł
        </label>
        <input
          id="carousel-post-filter-title"
          v-model="form.title"
          type="search"
          autocomplete="off"
          placeholder="Szukaj…"
          class="form-input w-[200px] py-1.5 text-sm"
        >
      </div>
      <div>
        <label
          for="carousel-post-filter-description"
          class="mb-1 block text-xs font-medium text-gray-600"
        >
          Opis
        </label>
        <input
          id="carousel-post-filter-description"
          v-model="form.description"
          type="search"
          autocomplete="off"
          placeholder="Szukaj…"
          class="form-input w-[200px] py-1.5 text-sm"
        >
      </div>
      <div>
        <label
          for="carousel-post-filter-site"
          class="mb-1 block text-xs font-medium text-gray-600"
        >
          Witryna
        </label>
        <site-context-select
          id="carousel-post-filter-site"
          v-model="form.site"
          :options="site_context_options"
          allow-blank
          blank-label="Wszystkie"
          class="w-[240px]"
          compact
        />
      </div>
      <button
        v-if="hasActiveFilters"
        type="button"
        class="text-sm text-gray-500 hover:text-gray-800"
        @click="resetFilters"
      >
        Wyczyść
      </button>
      <button class="btn-indigo ml-auto" @click="modalNew = true">
        + Nowy post karuzeli
      </button>
      <modal
        :open="modalNew"
        title="Dodaj post karuzeli"
        @close="modalNew = false"
      >
        <new-carousel-post
          :site_context_options="site_context_options"
          @success="modalNew = false"
        />
      </modal>
    </div>

    <p
      v-if="!canReorder"
      class="mb-3 text-xs text-gray-500"
    >
      Aby zmienić kolejność, wyczyść filtry tytułu i opisu.
    </p>

    <div class="overflow-hidden rounded-sm bg-white shadow-sm">
      <table
        class="w-full whitespace-nowrap"
        :class="{ 'is-dnd-dragging': canReorder && !!draggingSite }"
      >
        <thead>
          <tr class="text-left font-bold">
            <th class="w-px px-4 py-4">
              Kolejność
            </th>
            <th class="px-6 py-4">
              Tytuł
            </th>
            <th class="px-6 py-4">
              Opis
            </th>
            <th class="px-6 py-4">
              Zdjęcie
            </th>
            <th class="px-6 py-4">
              Witryna
            </th>
            <th class="px-6 py-4">
              Aktywny
            </th>
            <th class="w-px px-6 py-4" />
          </tr>
        </thead>

        <tbody v-if="!visibleSites.length">
          <tr>
            <td colspan="7" class="border-t px-6 py-6 text-center text-gray-500">
              {{ emptyMessage }}
            </td>
          </tr>
        </tbody>

        <template v-for="site in visibleSites" :key="site.value">
          <tbody>
            <tr>
              <th
                colspan="7"
                class="carousel-post-group-heading border-t px-6 py-2.5 text-left text-sm font-semibold text-gray-800"
                :style="siteBorderStyle(site.value)"
              >
                {{ site.label }}
              </th>
            </tr>
          </tbody>
          <draggable
            v-if="postsFor(site.value).length"
            v-model="groupedPosts[site.value]"
            tag="tbody"
            item-key="id"
            handle=".drag-handle"
            ghost-class="dnd-drop-ghost"
            chosen-class="dnd-chosen"
            :disabled="!canReorder"
            @start="onDragStart(site.value)"
            @end="onDragEnd(site.value)"
          >
            <template #item="{ element: post }">
              <tr
                :key="post.id"
                class="hover:bg-gray-50"
                :style="siteBorderStyle(post.site_context)"
              >
                <td class="border-t px-4 py-3">
                  <div class="flex items-center gap-2">
                    <div
                      v-if="canReorder"
                      class="drag-handle cursor-grab active:cursor-grabbing"
                      role="button"
                      aria-label="Przeciągnij, aby zmienić kolejność"
                      title="Przeciągnij, aby zmienić kolejność"
                    >
                      <icon name="grip" class="h-5 w-5 fill-gray-400" />
                    </div>
                    <span class="text-sm text-gray-600">{{ post.order }}</span>
                  </div>
                </td>
                <td class="border-t px-6 py-3">
                  {{ post.title || '(bez tytułu)' }}
                </td>
                <td class="border-t px-6 py-3">
                  <span class="max-w-md truncate block">{{ post.description || '—' }}</span>
                </td>
                <td class="border-t px-6 py-3">
                  <img
                    v-if="post.image_url"
                    :src="post.image_url"
                    :alt="post.title || 'Zdjęcie'"
                    class="h-16 aspect-[4/3] w-auto rounded object-cover"
                  >
                  <span v-else class="text-gray-400">Brak zdjęcia</span>
                </td>
                <td class="border-t px-6 py-3">
                  <site-context-select
                    :model-value="post.site_context"
                    :options="site_context_options"
                    :disabled="updatingSiteId === post.id"
                    class="max-w-[240px]"
                    compact
                    @update:model-value="updateSiteContext(post, $event)"
                  />
                </td>
                <td class="border-t px-6 py-3">
                  <toggle-switch
                    :model-value="post.active"
                    :disabled="togglingId === post.id"
                    @update:model-value="toggleActive(post)"
                  />
                </td>
                <td class="border-t px-6 py-3">
                  <div class="flex items-center gap-2">
                    <Link
                      class="btn-action-edit"
                      :href="routes.edit(post.id)"
                      title="Edytuj"
                    >
                      Edytuj
                    </Link>
                    <button
                      class="btn-action-delete"
                      type="button"
                      title="Usuń"
                      @click="destroy(post)"
                    >
                      Usuń
                    </button>
                  </div>
                </td>
              </tr>
            </template>
          </draggable>
          <tbody v-else>
            <tr>
              <td
                colspan="7"
                class="border-t px-6 py-4 text-sm text-gray-500"
                :style="siteBorderStyle(site.value)"
              >
                Brak postów karuzeli dla tej witryny.
              </td>
            </tr>
          </tbody>
        </template>
      </table>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/vue3';
import pickBy from 'lodash/pickBy';
import Modal from '@/Shared/Modal.vue';
import NewCarouselPost from '@/Pages/Admin/CarouselPosts/_New.vue';
import throttle from 'lodash/throttle';
import { createCarouselPostRouteHelper } from '@/utils/createCarouselPostRouteHelper';
import { createLayoutHelper } from '@/utils/layoutHelper';
import ToggleSwitch from '@/Shared/ToggleSwitch.vue';
import SiteContextSelect from '@/Shared/SiteContextSelect.vue';
import Icon from '@/Shared/Icon.vue';
import draggable from 'vuedraggable';
import axios from 'axios';

const SITE_COLOR_VARS = {
  code_medical_center: 'var(--color-code-medical-center)',
  infertility_treatment_clinic: 'var(--color-infertility-clinic)',
  institute: 'var(--color-institute)',
  family_clinic: 'var(--color-family-clinic)',
};

export default {
  components: {
    Head,
    Link,
    Modal,
    NewCarouselPost,
    ToggleSwitch,
    SiteContextSelect,
    Icon,
    draggable,
  },
  layout: createLayoutHelper,
  props: {
    // eslint-disable-next-line vue/prop-name-casing
    carousel_posts: {
      type: Object,
      required: true,
    },
    // eslint-disable-next-line vue/prop-name-casing
    site_context_options: {
      type: Array,
      default: () => [],
    },
    filters: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      form: {
        title: this.filters.title || '',
        description: this.filters.description || '',
        site: this.filters.site || null,
      },
      modalNew: false,
      groupedPosts: this.buildGroupedPosts(this.carousel_posts.data),
      draggingSite: null,
      togglingId: null,
      updatingSiteId: null,
    };
  },
  computed: {
    routes() {
      return createCarouselPostRouteHelper(null, this.$routes);
    },
    hasActiveFilters() {
      return !!(this.form.title?.trim() || this.form.description?.trim() || this.form.site);
    },
    hasTextFilters() {
      return !!(this.form.title?.trim() || this.form.description?.trim());
    },
    canReorder() {
      return !this.hasTextFilters;
    },
    emptyMessage() {
      return this.hasActiveFilters
        ? 'Brak postów karuzeli pasujących do filtrów.'
        : 'Brak postów karuzeli. Kliknij "Nowy post karuzeli".';
    },
    visibleSites() {
      let sites = this.site_context_options;
      if (this.form.site) {
        sites = sites.filter((site) => site.value === this.form.site);
      }
      if (this.hasTextFilters) {
        sites = sites.filter((site) => this.postsFor(site.value).length > 0);
      }
      return sites;
    },
  },
  watch: {
    form: {
      handler: throttle(function () {
        const query = pickBy({
          title: this.form.title?.trim(),
          description: this.form.description?.trim(),
          site: this.form.site || undefined,
        });
        this.$inertia.get(
          this.routes.index(Object.keys(query).length ? query : {}),
          {},
          {
            preserveState: true,
            preserveScroll: true,
            replace: true,
            only: ['carousel_posts', 'filters'],
          },
        );
      }, 150),
      deep: true,
    },
    '$page.url': {
      handler() {
        if (this.modalNew) {
          this.modalNew = false;
        }
      },
    },
    'carousel_posts.data': {
      handler(newData) {
        this.groupedPosts = this.buildGroupedPosts(newData);
      },
      deep: true,
    },
  },
  methods: {
    buildGroupedPosts(posts) {
      const grouped = {};
      (this.site_context_options || []).forEach((option) => {
        grouped[option.value] = [];
      });
      (posts || []).forEach((post) => {
        const key = post.site_context;
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(post);
      });
      return grouped;
    },
    postsFor(siteContext) {
      return this.groupedPosts[siteContext] || [];
    },
    findLocalPost(id) {
      const groups = Object.values(this.groupedPosts);
      for (let i = 0; i < groups.length; i += 1) {
        const found = groups[i].find((item) => item.id === id);
        if (found) return found;
      }
      return null;
    },
    resetFilters() {
      this.form.title = '';
      this.form.description = '';
      this.form.site = null;
    },
    siteBorderStyle(siteContext) {
      const colorVar = SITE_COLOR_VARS[siteContext];
      if (!colorVar) return null;
      return { borderLeft: `12px solid ${colorVar}` };
    },
    siteLabelFor(siteContext) {
      if (!siteContext) return '';
      const option = this.site_context_options.find((item) => item.value === siteContext);
      return option?.label || '';
    },
    updateSiteContext(post, siteContext) {
      if (!post.id || this.updatingSiteId) return;
      if ((post.site_context || null) === (siteContext || null)) return;

      this.updatingSiteId = post.id;
      const target = this.findLocalPost(post.id);
      const previousSiteContext = target?.site_context ?? post.site_context;

      if (target) {
        target.site_context = siteContext;
        target.site_context_label = this.siteLabelFor(siteContext);
      }

      this.$inertia.put(
        this.routes.update(post.id),
        { carousel_post: { site_context: siteContext } },
        {
          preserveScroll: true,
          preserveState: true,
          only: ['carousel_posts'],
          onError: () => {
            if (target) {
              target.site_context = previousSiteContext;
              target.site_context_label = this.siteLabelFor(previousSiteContext);
            }
          },
          onFinish: () => {
            this.updatingSiteId = null;
          },
        },
      );
    },
    toggleActive(post) {
      if (!post.id || this.togglingId) return;
      this.togglingId = post.id;
      const target = this.findLocalPost(post.id);
      if (target) target.active = !target.active;

      this.$inertia.put(
        this.routes.update(post.id),
        { carousel_post: { active: target ? target.active : !post.active } },
        {
          preserveScroll: true,
          preserveState: true,
          only: ['carousel_posts'],
          onError: () => {
            if (target) target.active = !target.active;
          },
          onFinish: () => {
            this.togglingId = null;
          },
        },
      );
    },
    destroy(post) {
      if (!post.id) return;
      if (confirm('Czy na pewno chcesz usunąć ten post karuzeli?')) {
        this.$inertia.delete(this.routes.destroy(post.id), {
          preserveScroll: true,
        });
      }
    },
    async onDragEnd(siteContext) {
      this.draggingSite = null;
      if (!this.canReorder) return;

      const posts = this.postsFor(siteContext);
      posts.forEach((post, index) => {
        post.order = index + 1;
      });
      const ids = posts.map((post) => post.id);

      try {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

        await axios.post(this.routes.reorder(), {
          ids,
        }, {
          headers: {
            'X-CSRF-Token': csrfToken,
          },
        });

        this.$inertia.reload({ only: ['carousel_posts'] });
      } catch (error) {
        console.error('Failed to reorder posts:', error);
        this.groupedPosts = this.buildGroupedPosts(this.carousel_posts.data);
        alert('Nie udało się zmienić kolejności postów. Spróbuj ponownie.');
      }
    },
    onDragStart(siteContext) {
      this.draggingSite = siteContext;
    },
  },
};
</script>

<style scoped>
th.carousel-post-group-heading {
  background-color: #f9fafb; /* gray-50 */
}
</style>
